import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useSocketStore } from "../store/socket";
import { useSockets } from "../providers/socket";
import useContactStore, { contactInitialState } from "../store/contact";
import useContactsStore from "../store/contacts";

const useDebounce = (callback: Function, delay: number) => {
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Cleanup the previous timeout on re-render
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const debouncedCallback = (...args: any) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // @ts-ignore
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  };

  return debouncedCallback;
};

type CleanupFn = () => void;
type CallbackFn = (message: any) => void;

export function useHandleNewMessage(options?: {
  callback: CallbackFn;
  cleanup?: CleanupFn;
  contactId?: string;
}) {
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const queryClient = useQueryClient();
  const callback = options?.callback || (() => {});
  const cleanup = options?.cleanup || (() => {});
  const contactId = options?.contactId || undefined;
  const { userSocket } = useSockets();
  const socketSessionReady = useSocketStore(
    (state) => state.socketSessionReady,
  );
  const updateContactsDb = useContactsStore((state) => state.updateContactsDb);

  useEffect(() => {
    if (!userSocket || !socketSessionReady) return;
    const handleNewMessage = (message: any) => {
      if (message.data) {
        if (!Array.isArray(message.data)) return;

        // Refetch the counts query, only we'll cause a re-render in the subscribed components if the data changes
        if (message.invalidateCounter) {
          queryClient.refetchQueries({
            queryKey: ["getCounts"],
            type: "active",
            exact: true,
          });
        }

        // If a contactId is provided, we only update the contact if the updated contact matches the provided contactId
        const updatedContact = message.data[0]?._id || undefined;
        if (contactId && updatedContact !== contactId) {
          return;
        }

        console.log("setting up new data: ", message.data?.length);
        const dataArray = Array.isArray(message.data) ? message.data : [];
        updateContactsDb(dataArray);
        if (message.newMessage) {
          callback({ typing: false });
        }
      }
    };

    const handleIndicator = (message: any) => {
      // Handles backend typing indicator events
      // if typing is true, we set a timeout to clear it after 60s in case a new message is not received
      const { data } = message;
      const isTyping = data?.typing || false;
      if (isTyping) {
        callback({ typing: true });

        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
        typingTimeoutRef.current = setTimeout(() => {
          callback({ typing: false });
        }, 60_000);
      } else {
        // explicitly stopped typing
        callback({ typing: false });
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = null;
        }
      }
    };
    userSocket.on("new-message", handleNewMessage);
    userSocket.on("typing", handleIndicator);
    return () => {
      console.log("cleaning up handler chats...");
      userSocket.off("new-message", handleNewMessage);
      userSocket.off("typing", handleIndicator);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (cleanup) cleanup();
    };
  }, [userSocket, socketSessionReady]);
}
export const useSelectedContactData = (id: string, type?: string) => {
  if (type === "web-chat") {
    console.log("is webchat");
  }

  const { contactsDbObject } = useContactsStore((s) => s);
  const item = id ? contactsDbObject[id] : undefined;
  const messages =
    type === "web-chat"
      ? item?.messages.filter((m) => !m.interactive?.data?.call) ?? []
      : item?.messages ?? [];

  return {
    ...contactInitialState,
    id: id ?? "",
    loading: false,
    contact: item?.contact ?? contactInitialState.contact,
    messages: messages,
    bookings: item?.bookings ?? [],
    escalations: item?.escalations ?? [],
    toolCalls: item?.toolCalls ?? [],
  };
};

export default useDebounce;
